import {
  followUpStatusLabel,
  type HwcgNoticeStepReportDto,
  type HwcgNoticeStepRecipientRow,
} from './hwcgNoticeStepReportModel';

export type HwcgContactNoticeStatus = {
  notificationId: string | null;
  sentAt: string | null;
  deliveryState: string | null;
  step1Response: string | null;
  step2Response: string | null;
  step2StatusLabel: string;
};

const EMPTY_STATUS: HwcgContactNoticeStatus = {
  notificationId: null,
  sentAt: null,
  deliveryState: null,
  step1Response: null,
  step2Response: null,
  step2StatusLabel: '—',
};

export function deliveryStateLabel(state: string | null | undefined): string {
  if (!state) return '—';
  if (state === 'acknowledged') return 'Acknowledged';
  if (state === 'delivered') return 'Delivered';
  if (state === 'sent') return 'Sent';
  if (state === 'failed') return 'Failed';
  if (state === 'unreachable') return 'Unreachable';
  return state;
}

/**
 * Join contact list rows to one or more hwcg-steps reports via userGuid and the
 * per-contact notification id map.
 */
export function mergeHwcgContactNoticeStatus(params: {
  contacts: { id: string; userGuid?: string | null }[];
  contactNotificationIds: Record<string, string>;
  reportsByNotificationId: Record<string, HwcgNoticeStepReportDto | undefined>;
}): Record<string, HwcgContactNoticeStatus> {
  const byUser = new Map<string, Map<string, HwcgNoticeStepRecipientRow>>();
  for (const [notificationId, report] of Object.entries(
    params.reportsByNotificationId
  )) {
    if (!report) continue;
    const users = new Map<string, HwcgNoticeStepRecipientRow>();
    for (const row of report.recipients) {
      users.set(row.userGuid, row);
    }
    byUser.set(notificationId, users);
  }

  const out: Record<string, HwcgContactNoticeStatus> = {};
  for (const contact of params.contacts) {
    const notificationId = params.contactNotificationIds[contact.id] ?? null;
    if (!notificationId || !contact.userGuid) {
      out[contact.id] = { ...EMPTY_STATUS, notificationId };
      continue;
    }
    const row = byUser.get(notificationId)?.get(contact.userGuid);
    if (!row) {
      out[contact.id] = { ...EMPTY_STATUS, notificationId };
      continue;
    }
    out[contact.id] = {
      notificationId,
      sentAt: row.sentAt ?? null,
      deliveryState: row.deliveryState ?? null,
      step1Response: row.step1Response,
      step2Response: row.step2Response,
      step2StatusLabel: followUpStatusLabel(row.step2Status),
    };
  }
  return out;
}

export function formatNoticeSentAt(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}
